// 時間軸分段（純函式）：本週、下週、各月份（確定日期與只到月份合為一段）、季、年、TBA。
// 「本週／下週」只在依發售日近到遠排序、而且是未發售的確定日期時使用；其他情況依月份分段。
import { epochDay } from '@dayone/core/time';
import type { CatalogItem } from './catalog.ts';

export type Section =
  | { kind: 'week' | 'next-week'; start: number; end: number }
  | { kind: 'month'; y: number; m: number }
  | { kind: 'quarter'; y: number; q: 1 | 2 | 3 | 4 }
  | { kind: 'year'; y: number }
  | { kind: 'tba' }
  /** 模糊日期過期很久（GDO-23） */
  | { kind: 'overdue' };

/** 「今天」起算的天數；非確定日期回傳 null */
export function daysUntil(item: Pick<CatalogItem, 'date'>, today: number): number | null {
  const d = item.date;
  return d.p === 'day' ? epochDay(d.y, d.m, d.d) - today : null;
}

/**
 * 一款遊戲屬於哪一段。`today` 為 null 時不分「本週／下週」（例如新到舊排序、已發售）。
 * 本週 = 今天起 7 天內（含今天），下週 = 第 8–14 天。
 */
export function sectionOf(item: CatalogItem, today: number | null): { key: string; section: Section } {
  if (item.overdue) return { key: 'overdue', section: { kind: 'overdue' } };
  const d = item.date;
  if (today !== null && (!item.released || item.releaseDay) && d.p === 'day') {
    const n = epochDay(d.y, d.m, d.d) - today;
    if (n >= 0 && n <= 6) return { key: 'week', section: { kind: 'week', start: today, end: today + 6 } };
    if (n >= 7 && n <= 13) {
      return { key: 'next-week', section: { kind: 'next-week', start: today + 7, end: today + 13 } };
    }
  }
  switch (d.p) {
    case 'day':
    case 'month':
      return { key: `${d.y}-${String(d.m).padStart(2, '0')}`, section: { kind: 'month', y: d.y, m: d.m } };
    case 'quarter':
      return { key: `${d.y}-Q${d.q}`, section: { kind: 'quarter', y: d.y, q: d.q } };
    case 'year':
      return { key: `${d.y}`, section: { kind: 'year', y: d.y } };
    case 'tba':
      return { key: 'TBA', section: { kind: 'tba' } };
  }
}

/** 輪播：本週發售（有主視覺的確定日期）優先；本週沒有時改為最受期待的未發售遊戲 */
export function heroPicks(
  items: readonly CatalogItem[],
  today: number,
  max = 5,
): { mode: 'week' | 'hype'; items: CatalogItem[] } | null {
  const candidates = items.filter((i) => (!i.released || i.releaseDay) && i.row.a);
  const week = candidates
    .filter((i) => {
      const n = daysUntil(i, today);
      return n !== null && n >= 0 && n <= 6;
    })
    .sort((a, b) => (b.row.hy ?? 0) - (a.row.hy ?? 0) || a.sortKey - b.sortKey);
  if (week.length) return { mode: 'week', items: week.slice(0, max) };
  const hyped = candidates.filter((i) => (i.row.hy ?? 0) > 0).sort((a, b) => (b.row.hy ?? 0) - (a.row.hy ?? 0));
  return hyped.length ? { mode: 'hype', items: hyped.slice(0, max) } : null;
}

/** 使用者所在時區的「今天」（epoch day）：倒數與本週／下週依使用者的日曆計算 */
export function localEpochDay(now: number = Date.now()): number {
  const d = new Date(now);
  return epochDay(d.getFullYear(), d.getMonth() + 1, d.getDate());
}
